import { io } from 'socket.io-client';
import { api, getToken, shouldSandbox } from './apiClient';
import { getSandboxUser, sandboxConversations, sandboxMessages, sandboxSendMessage } from './sandbox';

/**
 * Messaging: REST (/api/messages/*) + Socket.io real-time layer.
 *
 * Server contract (backend/src/socket.js):
 *  - handshake: io(url, { auth: { token } })
 *  - rooms:    emit('thread:join', conversationId) / 'thread:leave'
 *  - typing:   emit('thread:typing', { bookingId: conversationId, name })
 *  - inbound:  on('message:new', msg), on('notification:new', n),
 *              on('thread:typing', { bookingId, name })
 */

let socket = null;
const joinedThreads = new Set();
const joinedEvents = new Set();
let mapJoined = false;

function rejoinRooms() {
  joinedThreads.forEach((id) => socket?.emit('thread:join', id));
  joinedEvents.forEach((id) => socket?.emit('event:join', id));
  if (mapJoined) socket?.emit('map:join');
}

export function connectSocket() {
  const token = getToken();
  if (!token) return null;
  if (socket?.connected) return socket;
  if (socket) socket.disconnect();
  const url = process.env.REACT_APP_API_URL || window.location.origin;
  socket = io(url, {
    auth: { token },
    transports: ['websocket', 'polling'],
  });
  socket.on('connect', rejoinRooms);
  return socket;
}

export function getSocket() {
  return socket;
}

export function disconnectSocket() {
  if (socket) {
    socket.off('connect', rejoinRooms);
    socket.disconnect();
    socket = null;
  }
  joinedThreads.clear();
  joinedEvents.clear();
  mapJoined = false;
}

export function joinThread(conversationId) {
  if (!conversationId) return;
  joinedThreads.add(String(conversationId));
  if (socket?.connected) socket.emit('thread:join', conversationId);
}

export function leaveThread(conversationId) {
  joinedThreads.delete(String(conversationId));
  if (socket?.connected) socket.emit('thread:leave', conversationId);
}

export function sendTyping(conversationId, name) {
  socket?.emit('thread:typing', { bookingId: conversationId, name });
}

export function joinMap() {
  mapJoined = true;
  if (socket?.connected) socket.emit('map:join');
}

export function leaveMap() {
  mapJoined = false;
  if (socket?.connected) socket.emit('map:leave');
}

export function joinEvent(eventId) {
  if (!eventId) return;
  joinedEvents.add(String(eventId));
  if (socket?.connected) socket.emit('event:join', eventId);
}

export function leaveEvent(eventId) {
  joinedEvents.delete(String(eventId));
  if (socket?.connected) socket.emit('event:leave', eventId);
}

// ── REST ──────────────────────────────────────────────────────

/** Start (or resume) a conversation about a truck. Returns the conversation. */
export async function startConversation(truckId) {
  try {
    const { data } = await api.post('/api/messages/start', { truckId });
    return data;
  } catch (err) {
    if (shouldSandbox(err) && getSandboxUser()) return sandboxConversations()[0];
    throw err;
  }
}

export async function getConversations() {
  try {
    const { data } = await api.get('/api/messages/conversations');
    return Array.isArray(data) ? data : [];
  } catch (err) {
    if (shouldSandbox(err) && getSandboxUser()) return sandboxConversations();
    throw err;
  }
}

export async function getMessages(conversationId) {
  try {
    const { data } = await api.get(`/api/messages/${conversationId}`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    if (shouldSandbox(err) && getSandboxUser()) return sandboxMessages(conversationId);
    throw err;
  }
}

export async function sendMessage(conversationId, content) {
  try {
    const { data } = await api.post(`/api/messages/${conversationId}`, { content });
    return data;
  } catch (err) {
    if (shouldSandbox(err) && getSandboxUser()) return sandboxSendMessage(conversationId, content);
    throw err;
  }
}

/** Soft-delete: hides the message for the current user only. */
export async function deleteMessage(messageId) {
  try {
    const { data } = await api.delete(`/api/messages/${messageId}`);
    return data;
  } catch (err) {
    if (shouldSandbox(err) && getSandboxUser()) return { message: 'Message deleted' };
    throw err;
  }
}
